import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { CalculatorIcon } from '../icons';

export interface FloatingActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Show loading spinner
   * @default false
   */
  loading?: boolean;
  /**
   * Button size for FAB
   * @default 'lg'
   */
  size?: 'md' | 'lg';
  /**
   * Custom icon (defaults to CalculatorIcon)
   */
  icon?: React.ReactNode;
}

/**
 * A floating action button component optimized for mobile interfaces
 * Only renders on mobile devices (hidden on lg screens and up)
 */
const FloatingActionButton = forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
  (
    {
      children,
      className,
      loading = false,
      disabled = false,
      size = 'lg',
      icon,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'inline-flex items-center justify-center rounded-xl font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none shadow-lg hover:shadow-xl active:scale-95';

    // Mobile-only: hidden on lg screens and up
    const responsiveStyles = 'lg:hidden';

    const sizeStyles = {
      md: 'h-12 w-16 px-3',
      lg: 'h-14 w-20 px-4',
    };

    const iconSize = {
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          responsiveStyles,
          sizeStyles[size],
          // Gradient background matching the app's theme
          'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700',
          'text-white',
          'focus-visible:ring-orange-500 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900',
          // Fixed positioning for FAB
          'fixed bottom-6 right-6 z-50',
          // Enhanced shadow for FAB
          'shadow-orange-500/30 hover:shadow-orange-500/50 dark:shadow-orange-900/40',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <svg
            className={cn('animate-spin', iconSize[size])}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          icon || <CalculatorIcon className={iconSize[size]} />
        )}
        {children}
      </button>
    );
  }
);

FloatingActionButton.displayName = 'FloatingActionButton';

export { FloatingActionButton };